import type { FC } from "react"
import { useCallback, useEffect, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import classNames from "classnames"
import { debounce } from "lodash-es"
import { useSetState, useRequest, useCreation } from "ahooks"
import { Row, Col, Button, Input, message } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import {
  getIndustryByType,
  getClassifyByIndustry,
  searchBrandList,
  getAreaModuleTypes,
  updateBrand,
} from "@/api"
import type { IndustryClassifyVO, SearchBrandArgs, SearchBrandVO, RouterIdsVO } from "@/api"
import { useConfigModel } from "@/models"
import { headerTabs } from "@/constants/constant"
import { getBrandIcon, getPathByMenuKey } from "@/util/commonUtils"
import { accDiv } from "@/util/util"
import "./index.scss"
// ==============================================
import { UnlimitedChoice } from "@/components/FilterConditions"
// ==============================================
const pageSize = 10
const unlimitedName = "不限"
const unlimitedValue = ""
const unlimitedObj = {
  label: unlimitedName,
  code: unlimitedValue,
}
// ==============================================
interface stateVO {
  industryOptions: IndustryClassifyVO[]
  classifyOptions: IndustryClassifyVO[]
  brandFirstType: string[]
  brandSecondType: string[]
  // 选中的品牌id
  activeBrandId: string
  // 选中的品牌name
  activeBrandName: string
  // 查询已开通品牌名称
  searchValue: string
  // 已开通品牌
  havePageNum: number
  haveTotal: number
  haveList: SearchBrandVO[]
  // 未开通品牌
  notPageNum: number
  notTotal: number
  notList: SearchBrandVO[]
  // loading
  okLoading: boolean
}
interface BrandChoiceProps {
  // 沙盘类型
  sandTableType: string
  // 关闭
  onClose?: () => void
}
const BrandChoice: FC<BrandChoiceProps> = ({ sandTableType, onClose }) => {
  const { sandTableId, brandId } = useParams<RouterIdsVO>() as any
  const navigate = useNavigate()
  const { currentConfig } = useConfigModel()
  const classifyChoiceRef = useRef<any>()
  const brandName = currentConfig?.brandId === brandId ? currentConfig?.brandName || "" : ""
  const [state, set] = useSetState<stateVO>({
    activeBrandId: brandId,
    activeBrandName: brandName,
    industryOptions: [],
    classifyOptions: [],
    brandFirstType: [unlimitedValue],
    brandSecondType: [unlimitedValue],
    searchValue: "",
    havePageNum: 1,
    haveTotal: 0,
    notPageNum: 1,
    notTotal: 0,
    haveList: [],
    notList: [],
    okLoading: false,
  })
  const {
    industryOptions,
    classifyOptions,
    brandFirstType,
    brandSecondType,
    activeBrandId,
    activeBrandName,
    searchValue,
    havePageNum,
    haveTotal,
    notPageNum,
    notTotal,
    haveList,
    notList,
    okLoading,
  } = state
  const isDisabledNotLeft = notPageNum <= 1
  const isDisabledNotRight = notPageNum >= Math.ceil(notTotal / pageSize)
  const isDisabledHaveLeft = havePageNum <= 1
  const isDisabledHaveRight = havePageNum >= Math.ceil(haveTotal / pageSize)

  // 根据沙盘类型获取品牌行业
  useRequest(() => getIndustryByType(sandTableType), {
    onSuccess: (res) => {
      const result = res || []
      set({
        industryOptions: [unlimitedObj, ...result],
      })
    },
    onError: () => {
      set({
        industryOptions: [unlimitedObj],
      })
    },
  })

  // 根据品牌行业获取品牌分类
  const { run: classifyRun } = useRequest((industry: string) => getClassifyByIndustry(industry), {
    manual: true,
    onSuccess: (res) => {
      const result = res || []
      set({
        classifyOptions: [unlimitedObj, ...result],
      })
    },
    onError: () => {
      set({
        classifyOptions: [unlimitedObj],
      })
    },
  })

  // 获取品牌区域范围和模块权限
  const { run: jumpRun } = useRequest(getAreaModuleTypes, {
    manual: true,
    onSuccess: (res) => {
      set({
        okLoading: false,
      })
      const { area = [], modules = [] } = res || {}
      if (!area?.length) {
        message.error("没有获取到区域范围权限")
        return
      }
      if (!modules?.length) {
        message.error("没有获取到页面模块权限")
        return
      }
      // code
      const areaCode = area?.[0]?.children?.[0]?.areaCode || "100000"
      // modules
      const menuKeys = headerTabs.map((item) => item.value)
      const moduleType = modules?.[0]?.moduleType
      const subModuleType = modules?.[0]?.children?.[0]?.moduleType
      const path = getPathByMenuKey(menuKeys.includes(moduleType) ? moduleType : subModuleType)
      // 跳转页面
      navigate(`/${sandTableId}/${activeBrandId}/${areaCode}/${path}`)
      // 关闭
      onClose?.()
    },
    onError: () => {
      set({
        okLoading: false,
      })
    },
  })

  // 切换沙盘品牌
  const { run: updateRun } = useRequest(updateBrand, {
    manual: true,
    onSuccess: () => {
      // 获取品牌区域范围area和模块moduleTypes权限
      jumpRun(sandTableId)
    },
    onError: () => {
      set({
        okLoading: false,
      })
    },
  })

  // 获取沙盘品牌列表
  const queryRun = useCallback(
    (args: SearchBrandArgs) => {
      const { unlocked } = args
      searchBrandList(args)
        .then((res) => {
          const dataSource = (res?.rows || []).map((item) => ({
            ...item,
            logo: item.logo || getBrandIcon(item.brandFirstType),
          }))
          const total = res?.total || 0
          if (unlocked) {
            // 已开通品牌
            set({
              haveList: dataSource,
              haveTotal: total,
            })
          } else {
            // 未开通品牌
            set({
              notList: dataSource,
              notTotal: total,
            })
          }
        })
        .catch(() => {
          if (unlocked) {
            // 已开通品牌
            set({
              haveList: [],
              haveTotal: 0,
            })
          } else {
            // 未开通品牌
            set({
              notList: [],
              notTotal: 0,
            })
          }
        })
    },
    [set],
  )

  // 分类
  useEffect(() => {
    const industry = brandFirstType?.[0] || ""
    if (industry) {
      // 根据品牌行业获取品牌分类
      classifyRun(industry)
    } else {
      set({
        classifyOptions: [unlimitedObj],
      })
    }
  }, [brandFirstType, classifyRun, set])

  // 已开通品牌
  useEffect(() => {
    // 获取沙盘品牌列表
    queryRun({
      brandName: searchValue,
      unlocked: true,
      sandTableType,
      brandFirstType: brandFirstType?.[0] || "",
      brandSecondType: brandSecondType?.[0] || "",
      pageNum: havePageNum,
      pageSize,
    })
  }, [sandTableType, brandFirstType, brandSecondType, havePageNum, searchValue, queryRun])

  // 未开通品牌
  useEffect(() => {
    // 获取沙盘品牌列表
    queryRun({
      brandName: "",
      unlocked: false,
      sandTableType,
      brandFirstType: brandFirstType?.[0] || "",
      brandSecondType: brandSecondType?.[0] || "",
      pageNum: notPageNum,
      pageSize,
    })
  }, [sandTableType, brandFirstType, brandSecondType, notPageNum, queryRun])

  // 行业change
  const industryChange = useCallback(
    (options) => {
      set({
        brandFirstType: options,
        brandSecondType: [unlimitedValue],
        notPageNum: 1,
        havePageNum: 1,
      })
      classifyChoiceRef.current?.setValues([unlimitedValue])
    },
    [set],
  )

  // 分类change
  const classifyChange = useCallback(
    (options) => {
      set({
        brandSecondType: options,
        notPageNum: 1,
        havePageNum: 1,
      })
    },
    [set],
  )

  // 品牌change
  const brandChange = useCallback(
    (item) => {
      set({
        activeBrandId: item.brandId,
        activeBrandName: item.brandName,
      })
    },
    [set],
  )

  // 搜索change
  const searchChange = useCreation(() => {
    return debounce((event) => {
      const value = event.target.value || ""

      set({
        searchValue: value,
        havePageNum: 1,
      })
    }, 300)
  }, [set])

  // 分页
  const changeBrandList = useCallback(
    (type: string, count: number) => {
      set(((prev: any) => {
        const total = Math.ceil(accDiv(prev[`${type}Total`], pageSize))
        const page = prev[`${type}PageNum`] + count
        const result = page <= 0 ? 1 : page > total ? total : page

        return {
          [`${type}PageNum`]: result,
        }
      }) as any)
    },
    [set],
  )

  // 确定
  const onFinish = useCallback(() => {
    if (!activeBrandId) {
      message.error("请选择品牌。")
      return
    }
    if (activeBrandId === brandId) {
      // 关闭
      onClose?.()
      return
    }
    set({
      okLoading: true,
    })
    // 切换沙盘品牌
    updateRun({
      sandTableId,
      brandId: activeBrandId,
      brandName: activeBrandName,
    })
  }, [activeBrandId, brandId, activeBrandName, sandTableId, updateRun, onClose, set])

  return (
    <div className="brand-choice">
      <Input
        className="theme-input-allow-clear brand-choice-search"
        allowClear
        placeholder="输入已开通品牌名称查询"
        suffix={<SearchOutlined />}
        onChange={searchChange}
      />
      <div className="divider-line" />
      <Row className="screening-box industry-box">
        <Col span={4}>行业：</Col>
        <Col span={20}>
          <div className="choice-box">
            <UnlimitedChoice
              optionClass="brand-choice-option"
              multiple={false}
              colorType="green"
              title="行业"
              elementType="tag"
              defaultValue={brandFirstType}
              options={industryOptions}
              nameField="label"
              idField="code"
              unlimitedValue={unlimitedValue}
              unlimitedName={unlimitedName}
              onChange={industryChange}
            />
          </div>
        </Col>
      </Row>
      <Row className="screening-box classify-box">
        <Col span={4}>分类：</Col>
        <Col span={20}>
          <div className="choice-box">
            <UnlimitedChoice
              ref={classifyChoiceRef}
              optionClass="brand-choice-option"
              multiple={false}
              colorType="green"
              title="分类"
              elementType="tag"
              defaultValue={brandSecondType}
              options={classifyOptions}
              nameField="label"
              idField="code"
              unlimitedValue={unlimitedValue}
              unlimitedName={unlimitedName}
              onChange={classifyChange}
            />
          </div>
        </Col>
      </Row>
      <div className="divider-line" />
      <Row>
        <Col span={4}>未开通品牌：</Col>
        <Col span={20} className="brand-box">
          {!!notList?.length && (
            <div
              className={classNames("brand-btn", "brand-btn-left", {
                disabled: isDisabledNotLeft,
              })}
              onClick={isDisabledNotLeft ? undefined : () => changeBrandList("not", -1)}
            />
          )}
          <div className="brand-list">
            {notList.map((item) => (
              <div key={item.brandId} className="brand-list-item not-opened">
                <div
                  className="brand-icon"
                  style={{
                    backgroundImage: item.logo ? `url(${item.logo})` : "",
                  }}
                />
                <div>{item.brandName}</div>
              </div>
            ))}
          </div>
          {!!notList?.length && (
            <div
              className={classNames("brand-btn", "brand-btn-right", {
                disabled: isDisabledNotRight,
              })}
              onClick={isDisabledNotRight ? undefined : () => changeBrandList("not", 1)}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col span={4}>已开通品牌：</Col>
        <Col span={20} className="brand-box">
          {!!haveList?.length && (
            <div
              className={classNames("brand-btn", "brand-btn-left", {
                disabled: isDisabledHaveLeft,
              })}
              onClick={isDisabledHaveLeft ? undefined : () => changeBrandList("have", -1)}
            />
          )}
          <div className="brand-list">
            {haveList.map((item) => (
              <div
                key={item.brandId}
                className={classNames("brand-list-item", {
                  "active-brand": activeBrandId === item.brandId,
                })}
              >
                <div
                  className="brand-icon"
                  style={{
                    backgroundImage: item.logo ? `url(${item.logo})` : "",
                  }}
                  title={item.brandName}
                  onClick={() => brandChange(item)}
                />
                <div>{item.brandName}</div>
              </div>
            ))}
          </div>
          {!!haveList.length && (
            <div
              className={classNames("brand-btn", "brand-btn-right", {
                disabled: isDisabledHaveRight,
              })}
              onClick={isDisabledHaveRight ? undefined : () => changeBrandList("have", 1)}
            />
          )}
        </Col>
      </Row>
      <div className="divider-line" />
      <div className="btns">
        <Button className="theme-cancel-btn" onClick={onClose}>
          取消
        </Button>
        <Button className="theme-primary-btn" type="primary" loading={okLoading} onClick={onFinish}>
          确定
        </Button>
      </div>
    </div>
  )
}

export default BrandChoice
